import React, { useEffect, useMemo, useRef, useState } from "react";
import { chatApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, RotateCcw, User as UserIcon, Volume2, VolumeX, Play } from "lucide-react";
import ReactMarkdown from "react-markdown";

const MAIV_AVATAR = "/brand/maiv-avatar.png";
const VOICE_PREF_KEY = "tradingox_chat_voice_on";

// --- Web Speech API helpers ---------------------------------------------
const speechSupported = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

let _cachedVoice = null;
function pickMaivVoice() {
  if (!speechSupported()) return null;
  if (_cachedVoice) return _cachedVoice;
  const voices = window.speechSynthesis.getVoices() || [];
  // Prefer a warm-sounding English female voice; fall back to any English voice.
  const scored = voices
    .filter((v) => v.lang && v.lang.toLowerCase().startsWith("en"))
    .map((v) => {
      const n = v.name.toLowerCase();
      let s = 0;
      if (/samantha|jenny|zira|aria|salli|joanna|female/.test(n)) s += 5;
      if (v.lang.toLowerCase().includes("us")) s += 2;
      if (v.localService) s += 1;
      return { v, s };
    })
    .sort((a, b) => b.s - a.s);
  _cachedVoice = scored[0]?.v || voices[0] || null;
  return _cachedVoice;
}

// Strip markdown + emoji so Maiv reads naturally instead of "asterisk asterisk fees asterisk asterisk"
function cleanForSpeech(text) {
  return String(text || "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#>•\-]{1,3}\s+/g, "")
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

function speakText(text) {
  if (!speechSupported()) return;
  try {
    window.speechSynthesis.cancel();
    const clean = cleanForSpeech(text);
    if (!clean) return;
    const utter = new SpeechSynthesisUtterance(clean);
    const voice = pickMaivVoice();
    if (voice) utter.voice = voice;
    utter.rate = 1.02;
    utter.pitch = 1.05;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
  } catch (err) {
    console.debug("[Chatbot] speech synthesis failed", err);
  }
}

function stopSpeaking() {
  if (!speechSupported()) return;
  try { window.speechSynthesis.cancel(); } catch (err) { console.debug("[Chatbot] cancel failed", err); }
}

function MaivAvatar({ size = 36, ring = true }) {
  return (
    <span
      className={`relative inline-block overflow-hidden rounded-full ${ring ? "ring-2 ring-emerald-400/50 shadow-[0_0_20px_-4px_rgba(16,185,129,0.6)]" : ""}`}
      style={{ width: size, height: size }}
    >
      <img
        src={MAIV_AVATAR}
        alt="Maiv"
        className="w-full h-full object-cover"
        draggable={false}
      />
    </span>
  );
}

const ANON_SID_KEY = "tradingox_chat_sid";
const ANON_NAME_KEY = "tradingox_chat_name";

function getAnonSessionId() {
  try {
    let sid = localStorage.getItem(ANON_SID_KEY);
    if (!sid) {
      sid = `anon-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
      localStorage.setItem(ANON_SID_KEY, sid);
    }
    return sid;
  } catch (err) {
    console.debug("[Chatbot] localStorage unavailable, using ephemeral session", err);
    return null;
  }
}

function getAnonName() {
  try {
    return localStorage.getItem(ANON_NAME_KEY) || "";
  } catch (err) {
    console.debug("[Chatbot] localStorage read failed", err);
    return "";
  }
}

function saveAnonName(name) {
  try {
    localStorage.setItem(ANON_NAME_KEY, name);
  } catch (err) {
    console.debug("[Chatbot] localStorage write failed — name won't persist", err);
  }
}

let msgUidCounter = 0;
const nextMsgUid = () => `m${Date.now()}-${++msgUidCounter}`;

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce [animation-delay:-0.3s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce [animation-delay:-0.15s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce" />
    </div>
  );
}

export default function Chatbot({ user = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [firstName, setFirstName] = useState(null);
  // Anonymous visitors must type their name before they can chat.
  const [guestName, setGuestName] = useState(() => (user ? null : getAnonName()));
  const [nameInput, setNameInput] = useState("");
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem(VOICE_PREF_KEY) === "1"; } catch { return false; }
  });
  const scrollerRef = useRef(null);
  const nameInputRef = useRef(null);
  const anonSid = useMemo(() => (user ? null : getAnonSessionId()), [user]);
  const sessionIdRef = useRef(user ? `user:${user.user_id}` : anonSid);
  const needsName = !user && !guestName;
  const lastSpokenIdRef = useRef(null);

  // Prime the voice list on mount (Chrome loads voices async)
  useEffect(() => {
    if (!speechSupported()) return;
    const load = () => pickMaivVoice();
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      try { window.speechSynthesis.onvoiceschanged = null; } catch (err) { console.debug("[Chatbot] unbind voices cb", err); }
      stopSpeaking();
    };
  }, []);

  // Auto-speak new assistant replies when voice is on
  useEffect(() => {
    if (!voiceOn || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role !== "assistant") return;
    if (lastSpokenIdRef.current === last.id) return;
    lastSpokenIdRef.current = last.id;
    speakText(last.content);
  }, [messages, voiceOn]);

  // Stop speaking whenever the panel closes
  useEffect(() => {
    if (!open) stopSpeaking();
  }, [open]);

  const toggleVoice = () => {
    setVoiceOn((v) => {
      const next = !v;
      try { localStorage.setItem(VOICE_PREF_KEY, next ? "1" : "0"); } catch (err) { console.debug("[Chatbot] voice pref persist failed", err); }
      if (!next) stopSpeaking();
      return next;
    });
  };

  // Load history on first open
  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      try {
        const sid = user ? undefined : anonSid;
        const r = await chatApi.history(sid);
        if (r?.messages?.length) {
          setMessages(r.messages.map((m) => ({ id: nextMsgUid(), role: m.role, content: m.content })));
        }
        if (r?.first_name) setFirstName(r.first_name);
      } catch (err) {
        console.error("[Chatbot] Failed to load history:", err);
        // Chat is still usable — user can start a fresh conversation
      }
    })();
  }, [open, user, anonSid]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy || needsName) return;
    setInput("");
    setMessages((m) => [...m, { id: nextMsgUid(), role: "user", content: text }]);
    setBusy(true);
    try {
      const sid = user ? undefined : anonSid;
      const r = await chatApi.send(text, sid, user ? undefined : guestName);
      if (r?.first_name) setFirstName(r.first_name);
      if (r?.session_id) sessionIdRef.current = r.session_id;
      setMessages((m) => [...m, { id: nextMsgUid(), role: "assistant", content: r?.reply || "…" }]);
    } catch (err) {
      console.error("[Chatbot] Send failed:", err);
      setMessages((m) => [
        ...m,
        {
          id: nextMsgUid(),
          role: "assistant",
          content:
            "I couldn't reach the AI just now — try again in a moment. If it keeps happening, ping support.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try {
      const sid = user ? undefined : anonSid;
      await chatApi.reset(sid);
    } catch (err) {
      console.error("[Chatbot] Reset failed:", err);
    }
    setMessages([]);
  };

  const greetingLabel = firstName || (user?.name?.split(" ")[0]) || "friend";

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="chatbot-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] group flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_10px_40px_-10px_rgba(16,185,129,0.7)] pl-1.5 pr-4 py-1.5 font-semibold text-sm transition-all hover:-translate-y-0.5"
          aria-label="Open TradingoX chat with Maiv"
        >
          <span className="relative">
            <MaivAvatar size={38} />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-300 ring-2 ring-emerald-500 animate-pulse" />
          </span>
          <span className="hidden sm:inline">Ask Maiv</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-[60] w-[min(92vw,380px)] h-[min(80vh,560px)] rounded-2xl border border-white/10 bg-[#0D0E14]/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#0F111A] to-[#0D0E14]">
            <div className="flex items-center gap-2">
              <span className="relative">
                <MaivAvatar size={40} />
                <span className="absolute -bottom-0 -right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0D0E14]" />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-slate-100">Maiv · Virtual Assistant</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300">
                  online · claude sonnet 5
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {speechSupported() && (
                <button
                  data-testid="chatbot-voice-toggle"
                  onClick={toggleVoice}
                  title={voiceOn ? "Turn Maiv's voice off" : "Turn Maiv's voice on"}
                  className={`p-1.5 rounded-md transition ${
                    voiceOn
                      ? "text-emerald-300 bg-emerald-500/15 hover:bg-emerald-500/25"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
              )}
              <button
                data-testid="chatbot-reset"
                onClick={reset}
                title="Clear conversation"
                className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                data-testid="chatbot-close"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Scroll area */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {needsName ? (
              <div className="text-center py-6 space-y-4">
                <div className="mx-auto"><MaivAvatar size={72} /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-100">
                    Hi there! I'm Maiv, your virtual assistant 👋
                  </div>
                  <div className="text-xs text-slate-400 mt-1.5 max-w-[260px] mx-auto leading-relaxed">
                    Before we start, what's your <b className="text-emerald-300">first name</b>?
                    I'd love to greet you properly.
                  </div>
                </div>
                <form
                  className="mx-auto flex gap-2 max-w-[280px]"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const clean = nameInput.trim().replace(/[^A-Za-z' \-]/g, "").slice(0, 40);
                    if (!clean) return;
                    saveAnonName(clean);
                    setGuestName(clean);
                    setNameInput("");
                    // Nudge focus so user goes straight to typing a message
                    setTimeout(() => {
                      document.querySelector('[data-testid="chatbot-input"]')?.focus();
                    }, 50);
                  }}
                >
                  <Input
                    data-testid="chatbot-name-input"
                    ref={nameInputRef}
                    autoFocus
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="e.g. Charles"
                    maxLength={40}
                    className="bg-[#0D0E14] border-white/10 h-10 text-sm"
                  />
                  <Button
                    data-testid="chatbot-name-submit"
                    type="submit"
                    disabled={!nameInput.trim()}
                    className="h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs shrink-0"
                  >
                    Continue
                  </Button>
                </form>
                <div className="text-[10px] text-slate-500">
                  Only shared with Maiv — not stored on our servers.
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && !busy && (
              <div className="text-center py-8 space-y-3">
                <div className="mx-auto">
                  <MaivAvatar size={72} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-100">
                    Hey {greetingLabel} — I'm Maiv, your virtual assistant 👋
                  </div>
                  <div className="text-xs text-slate-400 mt-1 max-w-[260px] mx-auto leading-relaxed">
                    Ask me anything: TradingoX fees, your portfolio, scalping basics, or just say hi.
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 justify-center pt-2">
                  {[
                    "How do fees work?",
                    "Teach me scalping",
                    user ? "How's my portfolio?" : "How do I sign up?",
                  ].map((s) => (
                    <button
                      key={s}
                      data-testid={`chatbot-suggest-${s.slice(0, 15).replace(/\s+/g, "-")}`}
                      onClick={() => setInput(s)}
                      className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="shrink-0">
                    <MaivAvatar size={28} ring={false} />
                  </div>
                )}
                <div
                  className={`max-w-[78%] px-3 py-2 text-sm leading-relaxed rounded-2xl ${
                    m.role === "user"
                      ? "bg-emerald-500 text-black rounded-br-sm whitespace-pre-wrap"
                      : "bg-white/5 text-slate-100 rounded-bl-sm border border-white/5 chatbot-markdown"
                  }`}
                >
                  {m.role === "assistant" ? (
                    <>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                          strong: ({ children }) => <strong className="text-emerald-300 font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="text-slate-300">{children}</em>,
                          ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 mb-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 mb-2">{children}</ol>,
                          code: ({ children }) => <code className="px-1 py-0.5 rounded bg-black/40 text-[12px] font-mono">{children}</code>,
                          a: ({ href, children }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-300 underline underline-offset-2">
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {m.content}
                      </ReactMarkdown>
                      {speechSupported() && (
                        <button
                          data-testid={`chatbot-play-${m.id}`}
                          onClick={() => speakText(m.content)}
                          aria-label="Play this reply out loud"
                          title="Hear Maiv say this"
                          className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-emerald-300 transition"
                        >
                          <Play className="w-3 h-3" /> Play
                        </button>
                      )}
                    </>
                  ) : (
                    m.content
                  )}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-white/10 grid place-items-center shrink-0">
                    <UserIcon className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex gap-2 justify-start">
                <div className="shrink-0">
                  <MaivAvatar size={28} ring={false} />
                </div>
                <div className="bg-white/5 border border-white/5 rounded-2xl rounded-bl-sm">
                  <TypingDots />
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-white/10 bg-[#0B0C12]">
            <div className="flex items-center gap-2">
              <Input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                placeholder={
                  needsName ? "Enter your name above to start…" :
                  busy ? "Maiv is thinking…" : `Message Maiv…`
                }
                disabled={busy || needsName}
                className="bg-[#0D0E14] border-white/10 h-10 text-sm"
              />
              <Button
                data-testid="chatbot-send"
                onClick={send}
                disabled={busy || !input.trim() || needsName}
                className="h-10 w-10 p-0 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black shrink-0"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-1.5 text-[10px] text-slate-500 text-center">
              Maiv is an AI — verify critical info. Not investment advice.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
